import React from "react";
import { useStoreState } from "pullstate";

import { getDescriptiveFormatDuration } from "@/helpers/formatDuration";
import { formatDate } from "@/helpers/formatDate";
import { PlayerStatusTypes } from "@/helpers/pageHelpers/Common/interface";
import {
  PlayerStore,
  psChangeTime,
  psPauseAudio,
  psPlaySource,
} from "@/store/PlayerStore";
import PauseIcon from "@/icons/rounded-pause.svg";
import PlayIcon from "@/icons/rounded-play.svg";
import ShareIcon from "@/icons/large-rounded-share.svg";
import BookmarkButton from "@/components/BookmarkButton";
import IconButton from "@/components/IconButton";
import ProgressBar from "@/components/Players/ProgressBar";
import Spinner from "@/components/Spinner";
import { ContentSourceEnum } from "@/components/BookmarkButton/BookmarkButton.interface";
import { useDataLayer } from "@/helpers/useDataLayer";
import Button from "@/components/Button";
import { ProgressBarTypes } from "@/components/Players/ProgressBar/ProgressBar.interface";
import CheckIcon from "@/icons/smallCheckmark.svg";

import { EpisodePagePlayerInterface } from "./EpisodePagePlayerInterface.interface";
import styles from "./EpisodePagePlayer.module.scss";

export const EpisodePagePlayer: React.FC<EpisodePagePlayerInterface> = ({
  metaData,
  url,
  duration,
  longVersionDuration,
  publishedAt,
  bookmarked,
  shareAction,
  time,
  slug,
}) => {
  const { pushToDataLayer } = useDataLayer();
  const {
    currentSource,
    audioCurrentTime: currentTime,
    isLoading,
    status,
    listenedEpisodes,
  } = useStoreState(PlayerStore, (store) => ({
    audioCurrentTime: store.audioCurrentTime,
    currentSource: store.currentSource,
    isLoading:
      store.playerStatus === PlayerStatusTypes.Loading &&
      store.currentSource === url,
    status: store.playerStatus,
    listenedEpisodes: store.listenedEpisodes,
  }));

  const isActivePlayer = currentSource === url;
  const isPlaying = isActivePlayer && status === PlayerStatusTypes.Playing;
  const episodeTime = listenedEpisodes?.[slug as string]?.status || time;
  const defaultTime = episodeTime | 0;
  const longDuration = longVersionDuration ? longVersionDuration : duration;
  const isCompleted = longDuration - defaultTime <= 0;

  const play = () => {
    PlayerStore.update(psPlaySource(url, metaData, time));
    if (isCompleted) {
      PlayerStore.update(psChangeTime(0));
    }
  };

  const stop = () => {
    PlayerStore.update(psPauseAudio);
  };

  const renderPlayerControls = () => {
    if (isLoading) {
      return <Spinner />;
    }
    return isPlaying ? (
      <>
        <PauseIcon />
        Zatrzymaj
      </>
    ) : (
      <>
        <PlayIcon />
        Słuchaj
      </>
    );
  };

  const handleShare = () => {
    shareAction();
    pushToDataLayer("share");
  };

  return (
    <div className={styles.player}>
      <div className={styles.timeline}>
        <div className={styles.playtime}>
          <time className={styles.uploaded} dateTime={publishedAt}>
            {formatDate(publishedAt)}
          </time>

          {isCompleted && !isPlaying ? (
            <span className={styles.listened}>
              Odsłuchano <CheckIcon className={styles.icon} />
            </span>
          ) : (
            <span className={styles.duration}>
                {isPlaying || isActivePlayer
                    ? getDescriptiveFormatDuration(longDuration - currentTime)
                    : getDescriptiveFormatDuration(longDuration)}
            </span>
          )}
        </div>

        <ProgressBar
          isActivePlayer={isPlaying}
          duration={longDuration}
          currentTime={isPlaying || isActivePlayer ? currentTime : defaultTime}
          variant={ProgressBarTypes.RoundPrimary}
          className={styles.progressBar}
        />
      </div>

      <div className={styles.actions}>
        <IconButton
          className={styles.share}
          variant="square"
          size="big"
          onClick={handleShare}
        >
          <ShareIcon />
        </IconButton>
        <Button
          variant="playerPrimary"
          onClick={isPlaying ? stop : play}
          className={styles.play}
        >
          {renderPlayerControls()}
        </Button>
        <BookmarkButton
          slug={metaData.slug}
          source={ContentSourceEnum.Episodes}
          bookmarked={bookmarked}
          variant="square"
          size="big"
        />

        <audio src={url} className={styles.audio} preload="none">
          <source src={url} type="audio/mpeg" />
        </audio>
      </div>
    </div>
  );
};

export default EpisodePagePlayer;
